import { createORPCClient } from "@orpc/client";
import type { ContractRouterClient } from "@orpc/contract";
import { OpenAPILink } from "@orpc/openapi-client/fetch";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import type { Segment } from "#shared/segmentsTypes.ts";
import { segmentsContract } from "#shared/segmentsContract.ts";
import { useEnvironmentVariable } from "@squide/firefly";
import { useQueryClient, type InfiniteData, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { useMemo } from "react";
import { apiFetch } from "@packages/core-client/api/apiFetch.ts";
import { invalidate, queryPath } from "@packages/core-client/query/queryKeys.ts";
import type { CollectionPage } from "@packages/core-client/collection/useInfiniteCollection.ts";

function createSegmentsClient(baseUrl: string) {
    const link = new OpenAPILink(segmentsContract, {
        url: `${baseUrl}/api/segments`,
        fetch: apiFetch
    });

    const client: ContractRouterClient<typeof segmentsContract> = createORPCClient(link);
    const queryUtils = createTanstackQueryUtils(client, { path: queryPath("admin-settings", ["segments"]) });

    return { client, queryUtils };
}

export function useSegmentsClient() {
    const apiBaseUrl = useEnvironmentVariable("apiBaseUrl");

    return useMemo(() => createSegmentsClient(apiBaseUrl), [apiBaseUrl]);
}

export function useCreateSegmentMutationOptions() {
    const { queryUtils } = useSegmentsClient();

    return queryUtils.createSegment.mutationOptions({
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "segments")
    });
}

export function useUpdateSegmentMutationOptions() {
    const { queryUtils } = useSegmentsClient();

    return queryUtils.updateSegment.mutationOptions({
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "segments")
    });
}

export function useRemoveSegmentMutationOptions() {
    const { queryUtils } = useSegmentsClient();

    return queryUtils.removeSegment.mutationOptions({
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "segments")
    });
}

function compareVisible(a: Segment, b: Segment) {
    if (a.isStarred !== b.isStarred) {
        return a.isStarred ? -1 : 1;
    }
    if (a.name === b.name) {
        return 0;
    }

    return a.name < b.name ? -1 : 1;
}

function patchSegmentStarred(queryClient: QueryClient, listKey: QueryKey, segmentId: string, isStarred: boolean) {
    queryClient.setQueriesData<InfiniteData<CollectionPage<Segment>>>({ queryKey: listKey }, existing => {
        if (!existing) {
            return existing;
        }

        const sorted = existing.pages
            .flatMap(page => page.items)
            .map(segment => (segment.id === segmentId ? { ...segment, isStarred } : segment))
            .toSorted(compareVisible);

        let offset = 0;
        const pages = existing.pages.map(page => {
            const items = sorted.slice(offset, offset + page.items.length);
            offset += page.items.length;

            return { ...page, items };
        });

        return { ...existing, pages };
    });
}

export function useStarSegmentMutationOptions() {
    const { queryUtils } = useSegmentsClient();
    const queryClient = useQueryClient();
    const listKey = queryUtils.listVisibleSegments.key();

    return queryUtils.starSegment.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onMutate: ({ segmentId }) => patchSegmentStarred(queryClient, listKey, segmentId, true),
        onError: (_error, { segmentId }) => patchSegmentStarred(queryClient, listKey, segmentId, false),
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClientFromCallback }) => invalidate(queryClientFromCallback, "segments", { except: [listKey] })
    });
}

export function useUnstarSegmentMutationOptions() {
    const { queryUtils } = useSegmentsClient();
    const queryClient = useQueryClient();
    const listKey = queryUtils.listVisibleSegments.key();

    return queryUtils.unstarSegment.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onMutate: ({ segmentId }) => patchSegmentStarred(queryClient, listKey, segmentId, false),
        onError: (_error, { segmentId }) => patchSegmentStarred(queryClient, listKey, segmentId, true),
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClientFromCallback }) => invalidate(queryClientFromCallback, "segments", { except: [listKey] })
    });
}
